import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'
import { formatMoney } from "@/lib/money"

interface StripeConfigurationCardProps {
    processor: ProcessorDetails
}

const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const getAccountId = (options: Record<string, unknown> | null) => {
    return options?.accountId as string | undefined
}

const getRadarEnabled = (options: Record<string, unknown> | null) => {
    const radar = options?.radar as { enabled?: boolean } | undefined
    return radar?.enabled ?? false
}

export const StripeConfigurationCard = ({ processor }: StripeConfigurationCardProps) => {
    const { t } = useTranslation('processors')
    const accountId = getAccountId(processor.options)
    const radarEnabled = getRadarEnabled(processor.options)

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('stripeConfiguration')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">{t('accountId')}</p>
                        <p className="font-mono text-sm">{accountId || t('notConfigured')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('secretKey')}</p>
                        <p className="font-mono text-sm">••••••••••••••••</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('radarStatus')}</p>
                        {radarEnabled ? (
                            <Badge variant="default">{t('enabled')}</Badge>
                        ) : (
                            <Badge variant="outline">{t('disabled')}</Badge>
                        )}
                    </div>

                    {processor.baseCurrency && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('baseCurrency')}</p>
                            <p className="font-semibold">{processor.baseCurrency}</p>
                        </div>
                    )}

                    {processor.monthlyProcessingCapacity !== null && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('monthlyCapacity')}</p>
                            <p className="font-semibold">
                                {formatMoney(processor.monthlyProcessingCapacity, processor.baseCurrency || 'USD')}
                            </p>
                        </div>
                    )}

                    <div>
                        <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                        <p className="font-semibold">{formatDate(processor.createdAt)}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
                        <p className="font-semibold">{formatDate(processor.updatedAt)}</p>
                    </div>

                    {processor.supportedCurrencies && processor.supportedCurrencies.length > 0 && (
                        <div className="col-span-full">
                            <p className="text-sm text-muted-foreground mb-2">{t('supportedCurrencies')}</p>
                            <div className="flex flex-wrap gap-2">
                                {processor.supportedCurrencies.map((currency) => (
                                    <Badge key={currency} variant="outline">
                                        {currency}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}

